import { AppContext } from "App";

import { CancelError, CancellationToken } from "@multiweb/types/multiweb";
import { HeadersBuilder } from "Global/Base/Comunication/HeadersBuilder";
import { HTTPError, HTTPRequest } from "Global/Base/Comunication/HTTPRequest";
import { HTTPStatus } from "Global/Base/Comunication/HTTPStatus";
import { Deferred, PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { EOWException, OWExceptions } from "OW.Remoting/Basic/OWExceptions";
import { ASServices, getServiceURL } from "OW.Remoting/Basic/ServiceEndpoints";
import { ApplicationStorage } from "../ApplicationStorage";
import { HEADER_RIGHTS_SETTINGS_VERSION, HEADER_ROLES_NAME, HEADER_TRANSACTION_CONTEXT_ID_NAME } from "../Constants";
import { ICommunicationManager, IGroupingParams, RequestMethod } from "../ICommunicationManager";
import { EMPTY_REQUEST_CONTEXT, RequestContext } from "../RequestContext";

const OK_HTTP_CODES = [HTTPStatus.ok, HTTPStatus.created, HTTPStatus.noContent, HTTPStatus.partialContent];
const GROUP_THROTTLING_MS = 50; // TODO: jen pro grupované dotazy a ne pro všechny jako teď!

/**
 * @internal
 */
export interface IServiceRequest {
    functionName: string;
    functionBody?: any;
    callParams?: ICallParameters;
    groupingParams?: IGroupingParams;
    priority: number;
    cancellationToken: CancellationToken;
    result: Deferred<any>;
    resolved?: boolean;
    /** Editační kontext formuláře/framu */
    transactionContextId?: string;
    roles?: readonly string[];
    rightsSettingsVersion?: string;
}

/**
 * @internal
 */
export interface ICallParameters {
    /** Typ HTTP metody pro odeslání dat na AS (GET, POST) - defaultně POST */
    requestMethod?: RequestMethod;
    /** Název controlleru rozhraní webové služby, na které se mají požadavky posílat - defaultně ASService */
    serviceName?: string;
}

/**
 * Type used in mapOfRequestsWithContext.
 */
type requestCounterWithPromiseType = {
    /** Request counter for requests with the given context ID. */
    requestCounter: number;
    /** Promise that is resolved when the request counter is 0 (no other request with the given context ID is being processed). */
    promise: Deferred<void>;
};

export class CommunicationManager implements ICommunicationManager {
    private _callingInProcess: boolean = false;
    private _batchRequestsToCall: IServiceRequest[] = [];
    private _mapOfRequestsWithContext: Map<string, requestCounterWithPromiseType> = new Map();
    private readonly _webServiceUrl: string;
    private readonly _groupThrottlingTime: number;
    private readonly _cancelError: CancelError = new CancelError();

    constructor(webServiceUrl: string, groupThrottlingTime: number = GROUP_THROTTLING_MS) {
        this._webServiceUrl = webServiceUrl;
        this._groupThrottlingTime = groupThrottlingTime;
    }

    private static _getRequestUrl(baseUrl: string, functionName: string, prefixPath: string): string {
        return `${baseUrl}/${prefixPath}${functionName ? functionName.startsWith("?") ? functionName : `/${functionName}` : ""}`;
    }

    public jsonRequest(functionName: string, functionBody?: any, requestMethod: RequestMethod = "POST", prefixPath: string = "ASService.svc/rest",
        headers?: ({ [name: string]: any } | Headers | HeadersBuilder | undefined), requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<any> {
        const roles = AppContext.instance.configuration.legacyActiveRoles ? AppContext.instance.activeUserRolesIds : requestContext.roles;
        const { cancellationToken, getTransactionContextId } = requestContext;
        const transactionContextId = getTransactionContextId && getTransactionContextId();
        const combinedHeaders = new HeadersBuilder(HTTPRequest.jsonHeaders, { "AuthID": ApplicationStorage.get(`SpecKey-${AppContext.instance.appId}`) }, headers);
        if (roles?.length)
            combinedHeaders.set(HEADER_ROLES_NAME, roles.join(";"));
        if (requestContext.rightsSettingsVersion)
            combinedHeaders.set(HEADER_RIGHTS_SETTINGS_VERSION, requestContext.rightsSettingsVersion);
        if (transactionContextId) {
            combinedHeaders.set(HEADER_TRANSACTION_CONTEXT_ID_NAME, transactionContextId);
        }

        return HTTPRequest.request(
            requestMethod,
            CommunicationManager._getRequestUrl(this._webServiceUrl, functionName, prefixPath),
            JSON.stringify(functionBody),
            combinedHeaders,
            "text",
            OK_HTTP_CODES,
            cancellationToken,
        )
            .then((responseText) => !responseText ? null : JSON.parse(responseText))
            .catch((error) => CommunicationManager._errorHandler(error));
    }

    private static _errorHandler(error: Error): void {
        if (error instanceof HTTPError) {
            if (error.status === HTTPStatus.found) {
                const e = new EOWException(error.statusText);
                e.request = error.request;
                throw e;
            } else {
                throw CommunicationManager._parseError(error);
            }
        }

        throw error;
    }

    private static _parseError(error: HTTPError): Error {
        if (error.status === 500) {
            return OWExceptions.parse(error, error.request);
        } else {
            // Performs the function "reject" when this.status is different from 500
            const exception = new EOWException(error.status + " " + error.statusText);
            exception.httpErrorCode = error.status;
            exception.request = error.request;
            return exception;
        }
    }

    public callRequest(functionName: string, service: ASServices, requestMethod: RequestMethod, urlParameters?: { [paramName: string]: any }, functionBody?: any,
        priority?: number, groupingParams?: IGroupingParams, requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<any> {
        const { roles, rightsSettingsVersion, cancellationToken, getTransactionContextId } = requestContext;
        const result = PromiseUtils.defer<any>();
        const contextId = getTransactionContextId && getTransactionContextId();
        const request: IServiceRequest = {
            functionName: this._buildFunctionUrlWithParams(functionName, urlParameters),
            functionBody: functionBody,
            callParams: { requestMethod: requestMethod, serviceName: getServiceURL(service) },
            groupingParams: groupingParams,
            priority: priority || 1,
            cancellationToken: cancellationToken,
            result: result,
            transactionContextId: contextId,
            roles,
            rightsSettingsVersion,
        };

        if (contextId) {
            const requestsWithContext = this._mapOfRequestsWithContext.get(contextId);

            if (requestsWithContext) {
                requestsWithContext.requestCounter++;
            } else {
                this._mapOfRequestsWithContext.set(contextId, { requestCounter: 1, promise: undefined });
            }

            // Not a nice solution, but with one parameter in "then" block or with one "finally" block it's not working.
            const onRequestEnd = () => this._manageRequestsAndContextMap(request);
            result.promise.then(onRequestEnd, onRequestEnd);
        }

        if (!groupingParams) {
            this._processUngrouped(request);
        } else {
            this._batchRequestsToCall.push(request);
            if (!this._callingInProcess) {
                this._callingInProcess = true;
                self.setTimeout(() => this._processAllCalls(), this._groupThrottlingTime);
            }
        }

        return result.promise;
    }

    private _buildFunctionUrlWithParams(functionName: string, urlParameters?: { [paramName: string]: any }): string {
        return functionName + (!urlParameters ? "" : "?" + Reflect.ownKeys(urlParameters)
            .filter((k: string) => urlParameters[k] !== undefined)
            .map((k: string) => `${k}=${encodeURIComponent(urlParameters[k])}`)
            .join("&"));
    }

    public callGETRequest(functionName: string, service: ASServices, urlParameters?: { [paramName: string]: any }, priority?: number, requestContext?: RequestContext): Promise<any> {
        return this.callRequest(functionName, service, "GET", urlParameters, undefined, priority, undefined, requestContext);
    }

    public callPOSTRequest(functionName: string, service: ASServices, urlParameters?: { [paramName: string]: any }, functionBody?: any, priority?: number, requestContext?: RequestContext): Promise<any> {
        return this.callRequest(functionName, service, "POST", urlParameters, functionBody, priority, undefined, requestContext);
    }

    public callPATCHRequest(functionName: string, service: ASServices, urlParameters?: { [paramName: string]: any }, functionBody?: any, headers?: ({ [name: string]: any } | Headers | HeadersBuilder | undefined), _priority?: number, requestContext?: RequestContext): Promise<any> {
        return this.jsonRequest(this._buildFunctionUrlWithParams(functionName, urlParameters), functionBody, "PATCH", getServiceURL(service), headers, requestContext);
    }

    public callGroupedPOSTRequest(functionName: string, service: ASServices, urlParameters?: { [paramName: string]: any }, functionBody?: any, priority?: number, groupingParams?: IGroupingParams, requestContext?: RequestContext): Promise<any> {
        return this.callRequest(functionName, service, "POST", urlParameters, functionBody, priority, groupingParams === undefined ? { groupByBody: true } : groupingParams, requestContext);
    }

    public callGroupedGETRequest(functionName: string, service: ASServices, urlParameters?: { [paramName: string]: any }, priority?: number, groupingParams?: IGroupingParams, requestContext?: RequestContext): Promise<any> {
        return this.callRequest(functionName, service, "GET", urlParameters, undefined, priority, groupingParams === undefined ? { groupByFunction: true } : groupingParams, requestContext);
    }

    private _processAllCalls(): void {
        while (this._batchRequestsToCall.length > 0) {
            const temp = this._batchRequestsToCall;
            this._batchRequestsToCall = [];

            let groupedByPriority = this._groupBy(temp, function(item) {
                return [item.priority];
            });

            groupedByPriority = groupedByPriority.sort((i1, i2) => i2[0].priority - i1[0].priority); // sestupne

            for (const gbp of groupedByPriority) {
                this._processCallsWithSamePriority(gbp);
            }
        }
        this._callingInProcess = false;
    }

    /**
     * @param requests Požadavky se stejnou prioritou, ale potenciálně napříč různými rolemi a/nebo různým transakčním kontextem.
     * @private
     */
    private _processCallsWithSamePriority(requests: IServiceRequest[]): void {
        const getGroupKey = ({ roles, transactionContextId }: IServiceRequest) => {
            const sortedRoles = roles ? [...roles].sort() : [];
            return `${transactionContextId}-${sortedRoles}`;
        };
        const groupedByRolesAndTransactionContext = this._groupBy(requests, getGroupKey);
        groupedByRolesAndTransactionContext.forEach(group => this._processCallsWithSamePriorityAndRolesAndTransactionContext(group));
    }

    /**
     * @param requests Požadavky s touž prioritou, týmiž rolemi a týmž transakčním kontextem.
     * @private
     */
    private _processCallsWithSamePriorityAndRolesAndTransactionContext(requests: IServiceRequest[]): void {
        const groupedByFuncsAndGP = this._groupBy(requests, function(item) {
            return [item.functionName];
        });

        for (const group of groupedByFuncsAndGP) {
            const groupingParams = group[0].groupingParams;

            if (groupingParams && groupingParams.groupByFunction)
                this._processUniqueGroup(group); // všechny dotazy jsou seskupené podle volané funkce a stačí zavolat jeden
            else if (groupingParams)
                this._processGroup(group); // více dotazů (a jejich těl) se seskupí do jednoho
            else
                throw new Error("Ungrouped requests must be called directly");
        }
    }

    private _filterGroupUnique(group: IServiceRequest[]): IServiceRequest[] {
        if (group == null || group.length === 0)
            return [];

        // když vůbec nejsou zadané parametry pro grupování, tak se negrupuje
        const groupingParams = group[0].groupingParams;
        if (!groupingParams)
            return group;

        // grupování podle volané funkce
        if (groupingParams.groupByFunction)
            return group.filter((item1, index, array) => array.findIndex((item2) => item1.functionName === item2.functionName) === index);

        const bodyGroupParam = groupingParams.groupByBodyAttr;
        // pokud není nastaven specifický atribut, podle kterého by se to mělo dělat, tak se grupuje podle těla requestu (předpokladem je, že to není objekt)
        if (!bodyGroupParam)
            return group.filter((item1, index, array) => array.findIndex((item2) => item1.functionBody === item2.functionBody) === index);

        // jinak se grupuje podle zadaného atributu v těle requestu
        return group.filter((item1, index, array) => array.findIndex((item2) => item1.functionBody[bodyGroupParam] === item2.functionBody[bodyGroupParam]) === index);
    }

    private _processGroup(group: IServiceRequest[]): void {
        // odstraní se duplicitní položky grupovaného dotazu
        const uniqueGroup = this._filterGroupUnique(group);

        const firstItem = uniqueGroup[0];
        const groupingParams = firstItem.groupingParams;

        const items = [];
        for (const grp of uniqueGroup) {
            items.push(grp.functionBody);
        }

        let functionBody: any = {};
        if (groupingParams.groupedAttrName)
            functionBody[groupingParams.groupedAttrName] = items;
        else
            functionBody = items;

        this.jsonRequest(
            firstItem.functionName,
            functionBody,
            firstItem.callParams ? firstItem.callParams.requestMethod : undefined,
            firstItem.callParams ? firstItem.callParams.serviceName : undefined,
            undefined,
            {
                roles: firstItem.roles,
                rightsSettingsVersion: firstItem.rightsSettingsVersion,
                cancellationToken: CancellationToken.none,
                getTransactionContextId: () => firstItem.transactionContextId, //TODO: #34235
            },
        ).then((data) => {
            const foundItems = groupingParams.resultGroupedAttrName ? data[groupingParams.resultGroupedAttrName] : data;
            for (const dt of foundItems) {
                for (const request of group) {
                    if (request.cancellationToken.cancellationRequested) {
                        request.result.reject(this._cancelError);
                        continue;
                    }

                    const gp = request.groupingParams;
                    if (!request.resolved && (gp.groupByBodyAttr ? request.functionBody[gp.groupByBodyAttr] : request.functionBody).toUpperCase() === dt[gp.resultIDParameterName].toUpperCase()) {
                        request.result.resolve(dt);
                        request.resolved = true;
                    }
                }
            }

            for (const grp of group) {
                if (!grp.resolved) // zrušení requestů, které nějakou chybou nebyly dokončeny (alespoň nezůstanou viset donekonečna)
                    grp.result.reject(`Response doesn't contain data for grouped request of FunctionName: ${grp.functionName} and FunctionBody: ${grp.functionBody}`); //`
            }
        }).catch((err: any) => {
            group.forEach((request) => {
                request.result.reject(request.cancellationToken.cancellationRequested ? this._cancelError : err);
            });
        });
    }

    private _processUniqueGroup(group: IServiceRequest[]): void {
        if (group == null || group.length === 0)
            return;

        const request = group[0];

        this.jsonRequest(
            request.functionName,
            request.functionBody,
            request.callParams ? request.callParams.requestMethod : undefined,
            request.callParams ? request.callParams.serviceName : undefined,
            undefined,
            {
                roles: request.roles,
                cancellationToken: CancellationToken.none,
                getTransactionContextId: () => request.transactionContextId,
            },
        ).then((data) => {
            group.forEach((r) => {
                if (r.cancellationToken.cancellationRequested) {
                    r.result.reject(this._cancelError);
                    return;
                }
                r.result.resolve(data);
            });
        }).catch((err: any) => {
            group.forEach((r) => {
                r.result.reject(r.cancellationToken.cancellationRequested ? this._cancelError : err);
            });
        });
    }

    private _processUngrouped(request: IServiceRequest): void {
        if (request.cancellationToken.cancellationRequested) {
            request.result.reject(this._cancelError);
            return;
        }

        this.jsonRequest(
            request.functionName,
            request.functionBody,
            request.callParams ? request.callParams.requestMethod : undefined,
            request.callParams ? request.callParams.serviceName : undefined,
            undefined,
            {
                roles: request.roles,
                cancellationToken: request.cancellationToken,
                getTransactionContextId: () => request.transactionContextId,
            },
        ).then((data) => {
            request.result.resolve(data);
        }).catch((err: any) => {
            request.result.reject(request.cancellationToken.cancellationRequested ? this._cancelError : err);
        });
    }

    private _groupBy<T>(array: T[], f: (item: T) => any): T[][] {
        const groups: any = {};
        array.forEach(function(o) {
            const group = JSON.stringify(f(o));
            groups[group] = groups[group] || [];
            groups[group].push(o);
        });
        return Object.keys(groups).map(function(group) {
            return groups[group];
        });
    }

    /**
     * Method checks if there is some request with the context ID to be send. If so, it creates a waiting promise that is resolved when all requests with the context ID are send.
     * @param contextId The context ID.
     */
    public async waitForContextRequests(contextId: string): Promise<void> {
        const requestsWithContext = this._mapOfRequestsWithContext.get(contextId);

        if (!requestsWithContext) {
            return;
        }

        const waitingPromise = PromiseUtils.defer<void>();
        requestsWithContext.promise = waitingPromise;

        await waitingPromise.promise;
    }

    private _manageRequestsAndContextMap(request: IServiceRequest): void {
        if (request.transactionContextId) {
            const requestsWithContext = this._mapOfRequestsWithContext.get(request.transactionContextId);
            if (requestsWithContext) {
                requestsWithContext.requestCounter--;
                if (requestsWithContext.requestCounter <= 0) {
                    requestsWithContext.promise?.resolve();
                    this._mapOfRequestsWithContext.delete(request.transactionContextId);
                }
            }
        }
    }
}
